import { For, Show, createSignal } from "solid-js";

import { useContact, useContacts } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  value: () => string | null;
  onChange: (contactId: string | null, name: string | null) => void;
  selectedName?: () => string | null;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

export function ContactPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const contacts = useContacts(props.wsKey, () => ({
    q: query() || undefined,
    limit: 12,
  }));

  // Auto-resolve the selected contact's name when the parent didn't pre-supply
  // one (same pattern as UserPicker / AccountPicker).
  const resolved = useContact(props.wsKey, () => props.value() ?? "");

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (resolved.data) return resolved.data.full_name;
    return props.value()?.slice(0, 14) ?? "Contact";
  };

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const clear = () => {
    props.onChange(null, null);
    setQuery("");
  };

  const pick = (id: string, name: string) => {
    props.onChange(id, name);
    setQuery("");
    setOpen(false);
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search contacts…"}
              aria-label={props.label ?? "Search contacts"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setOpen(false), 150);
              }}
              autocomplete="off"
            />
            <Show when={open() && query().length > 0}>
              <ul
                role="listbox"
                aria-label="Contact matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={contacts.data?.items ?? []}>
                  {(c) => (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(c.id, c.full_name)}
                      >
                        <div class="font-medium">{c.full_name}</div>
                        <Show when={c.primary_email}>
                          <div class="text-xs opacity-60">{c.primary_email}</div>
                        </Show>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={(contacts.data?.items.length ?? 0) === 0 && !contacts.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No contacts match.</li>
                </Show>
                <Show when={contacts.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-primary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear contact"
            disabled={props.disabled}
            onClick={clear}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
